import { useState, useEffect } from 'react';
import { Trash2, MessageSquare, SquareStack } from 'lucide-react';

/**
 * Small floating menu that appears when the teacher hovers over a painted
 * highlight <mark> or an analyzed-sentence underline (or both — the common
 * case for a sentence that's been both highlighted and analyzed).
 *
 *   Highlight present     → Delete, Add note
 *   Analyzed present      → Go to analysis
 *
 * Positioned above the hovered mark's bounding rect.
 *
 * Props:
 *   rect:             DOMRect-like from getBoundingClientRect
 *   highlight:        the highlight TextSelection, or null
 *   analyzed:         the analyzed_sentence synthetic object, or null
 *                     (must include analysis_id + chapter_id)
 *   onDelete:         (highlight) => void
 *   onConvert:        (highlight, note) => void — converts to a comment
 *   onGoToAnalysis:   (analyzed) => void
 *   onDismiss:        () => void
 *   onCancelDismiss:  () => void — cancels a pending dismiss timer when the
 *                     cursor enters the menu
 *   onScheduleDismiss:() => void — re-schedules dismiss when the cursor
 *                     leaves the menu
 */
export default function HighlightHoverMenu({
  rect,
  highlight,
  analyzed,
  onDelete,
  onConvert,
  onGoToAnalysis,
  onDismiss,
  onCancelDismiss,
  onScheduleDismiss,
}) {
  const [mode, setMode] = useState('idle'); // idle | noting
  const [note, setNote] = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onDismiss(); };
    const onClick = (e) => {
      // Dismiss when clicking anywhere that isn't the menu or a painted mark.
      // (Hovering another mark will open a new menu instance anyway.)
      if (e.target.closest('[data-highlight-hover-menu]')) return;
      if (e.target.closest('mark[data-selection-id]')) return;
      onDismiss();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [onDismiss]);

  if (!rect || (!highlight && !analyzed)) return null;

  const x = rect.left + rect.width / 2;
  const y = rect.top - 6;

  return (
    <div
      data-highlight-hover-menu
      className="fixed z-50 pointer-events-auto"
      style={{ left: `${x}px`, top: `${y}px`, transform: 'translate(-50%, -100%)' }}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseEnter={() => onCancelDismiss?.()}
      onMouseLeave={() => onScheduleDismiss?.()}
    >
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl">
        {mode === 'idle' && (
          <div className="flex items-stretch divide-x divide-gray-200 dark:divide-gray-700">
            {highlight && (
              <button
                onClick={() => { onDelete(highlight); onDismiss(); }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 first:rounded-l-lg last:rounded-r-lg"
              >
                <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                Delete
              </button>
            )}
            {highlight && (
              <button
                onClick={() => setMode('noting')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 first:rounded-l-lg last:rounded-r-lg"
              >
                <MessageSquare className="w-3 h-3" strokeWidth={1.5} />
                Add note
              </button>
            )}
            {analyzed && (
              <button
                onClick={() => { onGoToAnalysis?.(analyzed); onDismiss(); }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-800 first:rounded-l-lg last:rounded-r-lg"
              >
                <SquareStack className="w-3 h-3" strokeWidth={1.5} />
                Go to analysis
              </button>
            )}
          </div>
        )}

        {mode === 'noting' && (
          <div className="p-3 w-72">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Convert to comment
            </label>
            <textarea
              autoFocus
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="w-full text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Your note about this passage…"
            />
            <div className="mt-2 flex justify-end gap-2">
              <button
                onClick={() => { setMode('idle'); setNote(''); }}
                className="text-xs px-2 py-1 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (note.trim()) {
                    onConvert(highlight, note.trim());
                    onDismiss();
                  }
                }}
                disabled={!note.trim()}
                className="text-xs px-3 py-1 rounded bg-gray-900 text-white dark:bg-white dark:text-gray-900 disabled:opacity-40"
              >
                Save
              </button>
            </div>
          </div>
        )}
      </div>
      <div
        className="w-0 h-0 mx-auto border-l-6 border-r-6 border-t-6 border-l-transparent border-r-transparent border-t-white dark:border-t-gray-900"
        style={{ filter: 'drop-shadow(0 1px 0 #e5e7eb)' }}
      />
    </div>
  );
}
